import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const ModalCadastroProduto = ({
  open,
  onClose,
  loadingProdutos = false,
  salvandoCadastro = false,
  produtos = [],
  produtosCadastrados = new Set(),
  cadastroSucesso = new Set(),
  cadastroEmAndamento = new Set(),
  cadastroError = "",
  idCrm = null,
  onCadastrar = () => {},
}) => {
  const [nomeEmpreendimento, setNomeEmpreendimento] = useState("");
  const [nomeTouched, setNomeTouched] = useState(false);
  const [buscaProduto, setBuscaProduto] = useState("");

  const resetFormulario = () => {
    setNomeEmpreendimento("");
    setNomeTouched(false);
    setBuscaProduto("");
  };

  useEffect(() => {
    if (open) {
      resetFormulario();
    }
  }, [open]);

  const normalizedCadastrados = useMemo(() => {
    const base = produtosCadastrados instanceof Set
      ? produtosCadastrados
      : new Set(produtosCadastrados || []);
    const sucesso = cadastroSucesso instanceof Set
      ? cadastroSucesso
      : new Set(cadastroSucesso || []);
    return new Set(
      [...base, ...sucesso].map((id) => (id != null ? String(id) : id))
    );
  }, [produtosCadastrados, cadastroSucesso]);

  const normalizedEmAndamento = useMemo(() => {
    const base = cadastroEmAndamento instanceof Set
      ? cadastroEmAndamento
      : new Set(cadastroEmAndamento || []);
    return new Set([...base].map((id) => (id != null ? String(id) : id)));
  }, [cadastroEmAndamento]);

  const cadastroSucessoCount = useMemo(() => {
    const base = cadastroSucesso instanceof Set
      ? cadastroSucesso
      : new Set(cadastroSucesso || []);
    return base.size;
  }, [cadastroSucesso]);

  const isCrmIndefinido = idCrm == null;
  const isCrmManual = Number(idCrm) === 0;
  const cadastroManualConcluido = isCrmManual && cadastroSucessoCount > 0;
  const successMessage =
    cadastroSucessoCount > 0
      ? cadastroSucessoCount === 1
        ? "Produto cadastrado com sucesso."
        : `${cadastroSucessoCount} produtos cadastrados com sucesso.`
      : "";
  const headerMessage = cadastroError
    ? { severity: "error", text: cadastroError }
    : successMessage
    ? { severity: "success", text: successMessage }
    : null;

  const primaryButtonSx = {
    backgroundColor: "#7b3ff6",
    color: "#fff",
    boxShadow: "0 6px 14px rgba(123, 63, 246, 0.25)",
    borderRadius: "999px",
    px: 2.5,
    transition: "background-color 180ms ease, box-shadow 180ms ease, transform 180ms ease",
    "&:hover": {
      backgroundColor: "#6a35e3",
      boxShadow: "0 8px 18px rgba(123, 63, 246, 0.32)",
      transform: "translateY(-1px)",
    },
    "&:disabled": {
      backgroundColor: "#d4c6fb",
      color: "#fff",
      boxShadow: "none",
    },
  };

  const resolveProdutoId = (produto, index = 0) => {
    return (
      produto?.id ??
      produto?.idProduto ??
      produto?.idEmpreendimentoCrm ??
      index
    );
  };

  const isNomeValido = nomeEmpreendimento.trim().length > 0;
  const isFormValido = isNomeValido;

  const showNomeError = nomeTouched && !isNomeValido;

  const produtosFiltrados = useMemo(() => {
    const termo = buscaProduto.trim().toLowerCase();
    const baseProdutos = Number(idCrm) === 1
      ? [...produtos].sort((a, b) => {
          const nomeA = String(a?.nome || "").trim();
          const nomeB = String(b?.nome || "").trim();
          return nomeA.localeCompare(nomeB, "pt-BR", { sensitivity: "base" });
        })
      : produtos;
    if (!termo) return baseProdutos;
    return baseProdutos.filter((produto) => {
      const nome = String(produto?.nome || "").toLowerCase();
      return nome.includes(termo);
    });
  }, [buscaProduto, produtos, idCrm]);

  const handleCadastrarNome = () => {
    setNomeTouched(true);
    if (!isFormValido) return;
    onCadastrar({
      nome: nomeEmpreendimento.trim(),
    });
  };

  const handleClose = () => {
    resetFormulario();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {"Cadastrar produtos"}
            </Typography>
          </Box>
          {headerMessage && (
            <Alert
              severity={headerMessage.severity}
              variant="outlined"
              sx={{
                justifySelf: "center",
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                py: 0.5,
                px: 1.5,
                alignItems: "center",
                "& .MuiAlert-message": { py: 0 },
                "@media (max-width:600px), (orientation: portrait)": {
                  position: "static",
                  left: "auto",
                  transform: "none",
                  border: "none",
                  px: 1,
                  "& .MuiAlert-message": { display: "none" },
                },
              }}
            >
              {headerMessage.text}
            </Alert>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <IconButton aria-label="Fechar" onClick={handleClose} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {isCrmIndefinido ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <CircularProgress size={28} />
            <Typography sx={{ mt: 2, color: "#666" }}>
              Carregando configuracao do CRM...
            </Typography>
          </Box>
        ) : isCrmManual ? (
          <Box sx={{ py: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Informe apenas o nome do produto para concluir o cadastro.
            </Typography>
            <TextField
              label="Nome do produto"
              value={nomeEmpreendimento}
              onChange={(e) => setNomeEmpreendimento(e.target.value)}
              onBlur={() => setNomeTouched(true)}
              fullWidth
              size="small"
              error={showNomeError}
              helperText={showNomeError ? "Informe um nome válido." : " "}
              disabled={salvandoCadastro}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                onClick={handleCadastrarNome}
                disabled={salvandoCadastro || !isFormValido || cadastroManualConcluido}
                sx={primaryButtonSx}
              >
                {salvandoCadastro ? "Salvando..." : "Cadastrar"}
              </Button>
            </Box>
          </Box>
        ) : loadingProdutos ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <CircularProgress size={36} />
            <Typography sx={{ mt: 2, color: "#666" }}>
              Carregando produtos...
            </Typography>
          </Box>
        ) : produtos.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Nenhum produto disponivel para cadastro.
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <TextField
                label="Buscar produto"
                placeholder="Digite o nome do produto"
                value={buscaProduto}
                onChange={(e) => setBuscaProduto(e.target.value)}
                fullWidth
                size="small"
              />
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Produto</TableCell>
                  <TableCell align="right">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {produtosFiltrados.map((produto, index) => {
                  const idProduto = resolveProdutoId(produto, index);
                  const idProdutoKey = idProduto != null ? String(idProduto) : "";
                  const isCadastrado = normalizedCadastrados.has(idProdutoKey);
                  const isCadastrando = normalizedEmAndamento.has(idProdutoKey);
                  const buttonLabel = isCadastrando
                    ? "Cadastrando..."
                    : isCadastrado
                    ? "Cadastrado"
                    : "Cadastrar";
                  const buttonStyles = isCadastrado || isCadastrando
                    ? {
                        bgcolor: "#9e9e9e",
                        color: "#fff",
                        "&:hover": { bgcolor: "#8d8d8d" },
                        cursor: "default",
                        borderRadius: "999px"
                      }
                    : {
                        ...primaryButtonSx,
                      };

                  return (
                    <TableRow
                      key={idProduto || `${produto?.nome}-${index}`}
                      sx={{
                        "&:nth-of-type(even)": {
                          backgroundColor: "rgba(0,0,0,0.02)",
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {produto?.nome || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => onCadastrar(produto)}
                          disabled={isCadastrado || isCadastrando}
                          sx={{ minWidth: 120, ...buttonStyles }}
                        >
                          {isCadastrando && (
                            <CircularProgress size={16} sx={{ mr: 1, color: "#fff" }} />
                          )}
                          {buttonLabel}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {produtosFiltrados.length === 0 && (
              <Box sx={{ py: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  Nenhum produto encontrado para a busca.
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ModalCadastroProduto;
